import type { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { InputManager } from "../core/InputManager";
import { PLAYER_CONFIG, type GameSettings } from "../game/GameConfig";
import {
  resolveCircleMotion,
  type CircleCollisionBody,
  type HorizontalMotion,
} from "../systems/CollisionSystem";

export interface ScreenShakeOffset {
  yaw: number;
  pitch: number;
}

export function calculateScreenShake(
  elapsedSeconds: number,
  durationSeconds: number,
  magnitude: number,
  result: ScreenShakeOffset = { yaw: 0, pitch: 0 },
): ScreenShakeOffset {
  if (durationSeconds <= 0 || elapsedSeconds < 0 || elapsedSeconds >= durationSeconds) {
    result.yaw = 0;
    result.pitch = 0;
    return result;
  }
  const strength = (1 - elapsedSeconds / durationSeconds) * magnitude;
  result.yaw = Math.sin(elapsedSeconds * 67) * strength;
  result.pitch = Math.cos(elapsedSeconds * 83) * strength * 0.65;
  return result;
}

export class PlayerController {
  private readonly velocity = Vector3.Zero();
  private yaw = 0;
  private pitch = 0;
  private bobTime = 0;
  private baseHeight: number = PLAYER_CONFIG.cameraHeight;
  private moving = false;
  private readonly frameMotion: HorizontalMotion = { x: 0, z: 0 };
  private readonly resolvedMotion: HorizontalMotion = { x: 0, z: 0 };
  private readonly shakeOffset: ScreenShakeOffset = { yaw: 0, pitch: 0 };
  private shakeElapsed = 0;
  private shakeDuration = 0;
  private shakeMagnitude = 0;
  private verticalLookLocked = false;

  public constructor(
    private readonly camera: UniversalCamera,
    private readonly input: InputManager,
    private settings: GameSettings,
  ) {}

  public update(
    deltaSeconds: number,
    movementEnabled: boolean,
    blockers: readonly CircleCollisionBody[] = [],
  ): void {
    const look = this.input.consumeLookDelta(this.settings.mouseSensitivity);
    if (movementEnabled) {
      this.yaw += look.x;
      if (!this.verticalLookLocked) {
        this.pitch = Math.max(-1.18, Math.min(1.18, this.pitch + look.y));
      }
    }
    calculateScreenShake(
      this.shakeElapsed,
      this.shakeDuration,
      this.shakeMagnitude,
      this.shakeOffset,
    );
    this.shakeElapsed += deltaSeconds;
    this.camera.rotation.set(
      this.verticalLookLocked ? 0 : this.pitch + this.shakeOffset.pitch,
      this.yaw + this.shakeOffset.yaw,
      0,
    );

    const virtualMovement = this.input.movementAxis;
    const inputX = movementEnabled
      ? Number(this.input.isHeld("KeyD")) - Number(this.input.isHeld("KeyA")) + virtualMovement.x
      : 0;
    const inputZ = movementEnabled
      ? Number(this.input.isHeld("KeyW")) - Number(this.input.isHeld("KeyS")) + virtualMovement.z
      : 0;
    const inputLength = Math.hypot(inputX, inputZ);
    const speed =
      this.input.isHeld("ShiftLeft") || this.input.isHeld("ShiftRight")
        ? PLAYER_CONFIG.sprintSpeed
        : PLAYER_CONFIG.walkSpeed;
    const sinYaw = Math.sin(this.yaw);
    const cosYaw = Math.cos(this.yaw);
    const targetX = ((sinYaw * inputZ + cosYaw * inputX) / Math.max(1, inputLength)) * speed;
    const targetZ = ((cosYaw * inputZ - sinYaw * inputX) / Math.max(1, inputLength)) * speed;
    const response = inputLength > 0 ? PLAYER_CONFIG.acceleration : PLAYER_CONFIG.deceleration;
    const blend = Math.min(1, response * deltaSeconds);
    this.velocity.x += (targetX - this.velocity.x) * blend;
    this.velocity.z += (targetZ - this.velocity.z) * blend;
    if (!movementEnabled) {
      this.velocity.x = 0;
      this.velocity.z = 0;
    }
    this.moving = this.velocity.lengthSquared() > 0.08;
    this.frameMotion.x = this.velocity.x * deltaSeconds;
    this.frameMotion.z = this.velocity.z * deltaSeconds;
    resolveCircleMotion(
      this.camera.position,
      this.frameMotion,
      PLAYER_CONFIG.collisionRadius,
      blockers,
      this.resolvedMotion,
    );
    this.camera.cameraDirection.x += this.resolvedMotion.x;
    this.camera.cameraDirection.z += this.resolvedMotion.z;

    if (this.settings.cameraBob && this.moving && movementEnabled) {
      this.bobTime += deltaSeconds * (speed > PLAYER_CONFIG.walkSpeed ? 12 : 9);
      this.camera.position.y = this.baseHeight + Math.sin(this.bobTime) * 0.035;
    } else {
      this.camera.position.y +=
        (this.baseHeight - this.camera.position.y) * Math.min(1, deltaSeconds * 10);
    }
  }

  public applySettings(settings: GameSettings): void {
    this.settings = settings;
  }

  public addRecoil(amount: number): void {
    if (this.verticalLookLocked) return;
    this.pitch = Math.max(-1.18, this.pitch - amount);
  }

  public shake(durationSeconds: number, magnitude: number): void {
    this.shakeElapsed = 0;
    this.shakeDuration = Math.max(0, durationSeconds);
    this.shakeMagnitude = Math.max(0, magnitude);
  }

  public lookToward(direction: Vector3): void {
    this.yaw = Math.atan2(direction.x, direction.z);
    this.pitch = 0;
  }

  public setVerticalLookLocked(locked: boolean): void {
    this.verticalLookLocked = locked;
    if (!locked) return;
    this.pitch = 0;
    this.camera.rotation.x = 0;
  }

  public setBaseHeight(height: number): void {
    this.baseHeight = height;
  }

  public get isMoving(): boolean {
    return this.moving;
  }
}
